import type { PromptInputMessage } from '@crowdex/ui/ai/prompt-input';
import type { ChatRequestOptions } from 'ai';
import { useEffect, useRef } from 'react';
import { useStore } from 'zustand';
import {
  conversationStateOf,
  type DesktopShellStore,
  type PendingSend,
} from '@/lib/desktop-shell/store';

/** A waiting message as it leaves: an edit drops the turns after it, for the server and the agent too. */
export function pendingSendRequest(pending: PendingSend): {
  message: PromptInputMessage;
  options?: ChatRequestOptions;
} {
  if (pending.kind === 'edited')
    return {
      message: pending.message,
      options: { ...pending.options, body: { ...pending.options?.body, rewind: true } },
    };
  return pending.options === undefined
    ? { message: pending.message }
    : { message: pending.message, options: pending.options };
}

/**
 * Sends each conversation's waiting message once what it waits for is done (`PendingSend.ready`:
 * its worktree made, its sandbox started) and its agent is ready. The shown conversation's leaves
 * through its chat on screen; one the user left leaves in the background, through its own chat on
 * its own connection (`sendInBackground`), without being shown again. A message only ever leaves
 * in its own conversation, and only once.
 */
export function usePendingMessageDrain({
  store,
  chatId,
  sendMessage,
  sendInBackground,
  ready,
}: {
  store: DesktopShellStore;
  /** The conversation shown, whose chat `sendMessage` sends in. */
  chatId: string;
  sendMessage: (message: PromptInputMessage, options?: ChatRequestOptions) => Promise<void>;
  /**
   * Sends in a conversation that is not shown: `take` hands the message over once it can leave
   * there (null when it no longer waits). Says whether it could (its agent and folder known).
   */
  sendInBackground: (
    id: string,
    take: () => ReturnType<typeof pendingSendRequest> | null,
  ) => Promise<boolean>;
  /** Whether the shown conversation's agent is ready. */
  ready: boolean;
}): void {
  const conversationStates = useStore(store, (state) => state.conversationStates);
  const shownPending = conversationStateOf({ conversationStates }, chatId).pendingSend;

  useEffect(() => {
    if (shownPending === null || !shownPending.ready || !ready) return;
    store.getState().setConversationState(chatId, { pendingSend: null });
    const { message, options } = pendingSendRequest(shownPending);
    if (options === undefined) void sendMessage(message);
    else void sendMessage(message, options);
  }, [chatId, ready, sendMessage, shownPending, store]);

  // The conversations whose background send is being prepared (its place read): one at a time.
  const leavingRef = useRef(new Set<string>());
  useEffect(() => {
    for (const [id, conversation] of Object.entries(conversationStates)) {
      const waiting = conversation.pendingSend;
      if (id === chatId || waiting === null || !waiting.ready || leavingRef.current.has(id))
        continue;
      leavingRef.current.add(id);
      // Still its message, and still not shown (else it leaves on screen): it is taken once.
      const take = () => {
        const state = store.getState();
        if (state.chatId === id || conversationStateOf(state, id).pendingSend !== waiting)
          return null;
        state.setConversationState(id, { pendingSend: null });
        return pendingSendRequest(waiting);
      };
      void sendInBackground(id, take)
        .catch(() => false)
        .finally(() => leavingRef.current.delete(id));
    }
  }, [chatId, conversationStates, sendInBackground, store]);
}
